import React, { useEffect, useRef, useState } from 'react';
import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import { ClientMatchState } from '../../state/roomStore';
import { createCardSprite, createJokerSprite, loadSuitTextures } from '../../game/sprites';
import { announceHandType, dynamicScreenShake } from '../../utils/gameJuice';
import { JokerDefinition, describeJokerEffect } from '@solante/shared';
import { useOrientation } from '../../hooks/useMobile';

interface Props {
  state?: ClientMatchState;
  selectedCardIds?: string[];
  timeRemainingSeconds?: number | null;
  jokers?: JokerDefinition[];
  onCardClick?: (cardId: string) => void;
  isInteractive?: boolean;
  activatedJokerIds?: string[];
}

// Track if textures are loaded globally (persists across remounts)
let texturesLoaded = false;

// Type for tracking active combo animation
interface ActiveComboAnimation {
  ticker: (delta: number) => void;
  elements: Container[];
}

export const GameCanvas: React.FC<Props> = ({
  state,
  selectedCardIds = [],
  timeRemainingSeconds = null,
  jokers = [],
  onCardClick,
  isInteractive = false,
  activatedJokerIds = [],
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const appRef = useRef<Application | null>(null);
  const cardsLayerRef = useRef<Container | null>(null);
  const jokersLayerRef = useRef<Container | null>(null);
  const effectsLayerRef = useRef<Container | null>(null);
  const tooltipLayerRef = useRef<Container | null>(null);
  const titleRef = useRef<Text | null>(null);
  const drawLabelRef = useRef<Text | null>(null);
  const discardLabelRef = useRef<Text | null>(null);
  const previousSignatureRef = useRef<string>('');
  const previousComboRef = useRef<string>('');
  const previousJokersRef = useRef<string>('');
  const activeJokerRef = useRef<JokerDefinition | null>(null);
  const activeComboAnimationRef = useRef<ActiveComboAnimation | null>(null);
  const activeTickersRef = useRef(new Set<(delta: number) => void>());
  const [texturesReady, setTexturesReady] = useState(texturesLoaded);
  const [viewportWidth, setViewportWidth] = useState(720);
  const [viewportHeight, setViewportHeight] = useState(480);
  const { isPortrait } = useOrientation();

  // Track viewport/container dimensions for responsive card positioning (including PSG1)
  // ResizeObserver fires on flex reflows (not just window resize), fixing stale dimensions
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setViewportWidth(entry.contentRect.width);
        setViewportHeight(entry.contentRect.height);
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!containerRef.current || appRef.current) return;

    const app = new Application({
      backgroundAlpha: 0,
      antialias: true,
      resizeTo: containerRef.current,
      resolution: window.devicePixelRatio,
      autoDensity: true,
    });

    containerRef.current.appendChild(app.view as HTMLCanvasElement);

    // Load suit textures and signal when ready
    let mounted = true;
    if (!texturesLoaded) {
      loadSuitTextures().then(() => {
        texturesLoaded = true;
        if (mounted) {
          setTexturesReady(true);
        }
      });
    }

    // Background with gradient
    const background = new Graphics();
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    // Dark space background
    background.beginFill(0x050810, 0.95);
    background.drawRoundedRect(0, 0, width, height, 24);
    background.endFill();

    // Subtle nebula effect
    background.beginFill(0x1a0a2e, 0.3);
    background.drawEllipse(width * 0.7, height * 0.3, 200, 150);
    background.endFill();
    background.beginFill(0x0a1a2e, 0.25);
    background.drawEllipse(width * 0.3, height * 0.7, 150, 100);
    background.endFill();

    // Star field (random dots)
    for (let i = 0; i < 30; i++) {
      const starX = Math.random() * width;
      const starY = Math.random() * height;
      const starSize = Math.random() * 1.5 + 0.5;
      const starAlpha = Math.random() * 0.4 + 0.2;
      background.beginFill(0xffffff, starAlpha);
      background.drawCircle(starX, starY, starSize);
      background.endFill();
    }

    app.stage.addChild(background);

    // Title with arcade style
    const title = new Text(
      '✦ SOLANTE DRAW ✦',
      new TextStyle({
        fontFamily: '"Press Start 2P", monospace',
        fontSize: 14,
        fill: 0x4ef6ff,
        letterSpacing: 2,
        dropShadow: true,
        dropShadowColor: 0x4ef6ff,
        dropShadowBlur: 8,
        dropShadowDistance: 0,
      }),
    );
    title.position.set(24, 16);
    app.stage.addChild(title);
    titleRef.current = title;

    const drawLabel = new Text(
      '◈ DECK —',
      new TextStyle({
        fontFamily: '"Space Grotesk", system-ui',
        fontSize: 13,
        fill: 0x9eb6ff,
        letterSpacing: 1,
      }),
    );
    drawLabel.alpha = 0.7;
    drawLabel.position.set(24, 48);
    app.stage.addChild(drawLabel);
    drawLabelRef.current = drawLabel;

    const discardLabel = new Text(
      '◈ VOID —',
      new TextStyle({
        fontFamily: '"Space Grotesk", system-ui',
        fontSize: 13,
        fill: 0xff9bb8,
        letterSpacing: 1,
      }),
    );
    discardLabel.alpha = 0.5;
    discardLabel.position.set(24, 72);
    app.stage.addChild(discardLabel);
    discardLabelRef.current = discardLabel;

    // Jokers layer (added FIRST so it renders UNDERNEATH cards)
    const jokersLayer = new Container();
    jokersLayer.position.set(0, 0);
    app.stage.addChild(jokersLayer);
    jokersLayerRef.current = jokersLayer;

    // Cards layer (added AFTER jokers so it renders ON TOP)
    const cardsLayer = new Container();
    cardsLayer.position.set(0, 0);
    app.stage.addChild(cardsLayer);
    cardsLayerRef.current = cardsLayer;

    // Effects layer (for combo celebrations - topmost)
    const effectsLayer = new Container();
    effectsLayer.position.set(0, 0);
    app.stage.addChild(effectsLayer);
    effectsLayerRef.current = effectsLayer;

    // Tooltip layer (always on top)
    const tooltipLayer = new Container();
    tooltipLayer.visible = false;
    app.stage.addChild(tooltipLayer);
    tooltipLayerRef.current = tooltipLayer;

    appRef.current = app;

    return () => {
      mounted = false;
      // Cancel all tracked ticker animations
      for (const tick of activeTickersRef.current) {
        app.ticker.remove(tick);
      }
      activeTickersRef.current.clear();
      activeComboAnimationRef.current = null;
      app.destroy(true, true);
      appRef.current = null;
      cardsLayerRef.current = null;
      jokersLayerRef.current = null;
      effectsLayerRef.current = null;
      tooltipLayerRef.current = null;
      titleRef.current = null;
      drawLabelRef.current = null;
      discardLabelRef.current = null;
    };
  }, []);

  // Animate cards with bounce
  const animateCardIn = (sprite: Container, order: number, shouldAnimate: boolean) => {
    const app = appRef.current;
    if (!app || !shouldAnimate) {
      sprite.alpha = 1;
      return;
    }
    const targetY = sprite.y;
    const targetScale = sprite.scale.x;
    sprite.alpha = 0;
    sprite.y = targetY + 50;
    sprite.scale.set(targetScale * 0.8);
    let elapsed = 0;
    const delay = order * 0.08;
    const duration = 0.4;

    const tick = (delta: number) => {
      elapsed += delta / 60;
      if (elapsed < delay) return;
      const t = Math.min(1, (elapsed - delay) / duration);
      // Elastic ease out
      const eased =
        t === 1 ? 1 : 1 - Math.pow(2, -10 * t) * Math.cos((t * 10 - 0.75) * ((2 * Math.PI) / 3));

      sprite.alpha = Math.min(1, t * 2);
      sprite.y = targetY + (1 - eased) * 50;
      sprite.scale.set(targetScale * (0.8 + 0.2 * eased));

      if (t >= 1) {
        app.ticker.remove(tick);
        activeTickersRef.current.delete(tick);
        sprite.y = targetY;
        sprite.alpha = 1;
        sprite.scale.set(targetScale);
      }
    };
    activeTickersRef.current.add(tick);
    app.ticker.add(tick);
  };

  // Show combo celebration
  const showComboCelebration = (comboName: string, score: number) => {
    const app = appRef.current;
    const effectsLayer = effectsLayerRef.current;
    if (!app || !effectsLayer) return;

    // Cancel any existing combo animation to prevent overlap
    if (activeComboAnimationRef.current) {
      app.ticker.remove(activeComboAnimationRef.current.ticker);
      activeComboAnimationRef.current.elements.forEach((el) => {
        if (effectsLayer.children.includes(el)) {
          effectsLayer.removeChild(el);
        }
      });
      activeComboAnimationRef.current = null;
    }

    const container = containerRef.current;
    if (!container) return;
    const centerX = container.clientWidth / 2;
    const centerY = container.clientHeight / 2 - 40;

    // Combo text
    const comboText = new Text(
      comboName.toUpperCase(),
      new TextStyle({
        fontFamily: '"Press Start 2P", monospace',
        fontSize: 20,
        fill: 0xffffff,
        letterSpacing: 3,
        dropShadow: true,
        dropShadowColor: getComboColor(comboName),
        dropShadowBlur: 12,
        dropShadowDistance: 0,
        stroke: getComboColor(comboName),
        strokeThickness: 2,
      }),
    );
    comboText.anchor.set(0.5);
    comboText.position.set(centerX, centerY);
    comboText.alpha = 0;
    comboText.scale.set(0.5);
    effectsLayer.addChild(comboText);

    // Score text
    const scoreText = new Text(
      `+${score}`,
      new TextStyle({
        fontFamily: '"Space Grotesk", system-ui',
        fontSize: 28,
        fill: 0xfcd34d,
        fontWeight: 'bold',
        dropShadow: true,
        dropShadowColor: 0xf59e0b,
        dropShadowBlur: 8,
        dropShadowDistance: 0,
      }),
    );
    scoreText.anchor.set(0.5);
    scoreText.position.set(centerX, centerY + 40);
    scoreText.alpha = 0;
    effectsLayer.addChild(scoreText);

    // Particles
    const particles: Graphics[] = [];
    const particleCount = 12;
    for (let i = 0; i < particleCount; i++) {
      const particle = new Graphics();
      particle.beginFill(getComboColor(comboName));
      particle.drawCircle(0, 0, Math.random() * 4 + 2);
      particle.endFill();
      particle.position.set(centerX, centerY);
      particle.alpha = 0;
      effectsLayer.addChild(particle);
      particles.push(particle);
    }

    // Track all elements for cleanup
    const allElements: Container[] = [comboText, scoreText, ...particles];

    // Animation
    let elapsed = 0;
    const duration = 1.5;
    const tick = (delta: number) => {
      elapsed += delta / 60;
      const t = elapsed / duration;

      // Text animation
      if (t < 0.3) {
        const popT = t / 0.3;
        comboText.scale.set(0.5 + popT * 0.8);
        comboText.alpha = popT;
        scoreText.alpha = popT * 0.5;
      } else if (t < 0.7) {
        comboText.scale.set(1.3 - (t - 0.3) * 0.75);
        comboText.alpha = 1;
        scoreText.alpha = 1;
      } else {
        const fadeT = (t - 0.7) / 0.3;
        comboText.alpha = 1 - fadeT;
        scoreText.alpha = 1 - fadeT;
        comboText.y = centerY - fadeT * 30;
        scoreText.y = centerY + 40 - fadeT * 30;
      }

      // Particle animation
      particles.forEach((particle, i) => {
        const angle = (i / particleCount) * Math.PI * 2;
        const speed = 80 + Math.random() * 40;
        const dist = t * speed;
        particle.x = centerX + Math.cos(angle) * dist;
        particle.y = centerY + Math.sin(angle) * dist;
        particle.alpha = t < 0.2 ? t * 5 : Math.max(0, 1 - (t - 0.2) / 0.8);
        particle.scale.set(1 - t * 0.5);
      });

      if (t >= 1) {
        app.ticker.remove(tick);
        activeTickersRef.current.delete(tick);
        allElements.forEach((el) => {
          if (effectsLayer.children.includes(el)) {
            effectsLayer.removeChild(el);
          }
        });
        activeComboAnimationRef.current = null;
      }
    };

    // Store active animation for potential cancellation
    activeComboAnimationRef.current = { ticker: tick, elements: allElements };
    activeTickersRef.current.add(tick);
    app.ticker.add(tick);
  };

  // Show joker tooltip
  const showJokerTooltip = (joker: JokerDefinition, x: number, y: number) => {
    const tooltipLayer = tooltipLayerRef.current;
    const container = containerRef.current;
    if (!tooltipLayer || !container) return;

    tooltipLayer.removeChildren();
    activeJokerRef.current = joker;

    const padding = 12;
    const width = 200; // Slightly wider for better text wrapping
    const effectSpacing = 4; // Spacing between effect lines

    // Create effect texts first to measure their actual heights
    const effectTexts: Text[] = [];
    let totalEffectsHeight = 0;

    if (joker.effects && joker.effects.length > 0) {
      joker.effects.forEach((effect) => {
        const effectText = new Text(
          `• ${describeJokerEffect(effect)}`,
          new TextStyle({
            fontFamily: 'system-ui',
            fontSize: 10,
            fill: 0x4ef6ff,
            wordWrap: true,
            wordWrapWidth: width - padding * 2,
          }),
        );
        effectTexts.push(effectText);
        totalEffectsHeight += effectText.height + effectSpacing;
      });
    }

    // Calculate height based on actual text heights
    // 42 = padding (12) + name height (~14) + rarity height (~12) + spacing (4)
    const height = 42 + totalEffectsHeight + padding;

    // Position tooltip - keep within canvas bounds
    let tooltipX = x + 15;
    let tooltipY = y - height / 2;
    if (tooltipX + width > container.clientWidth - 10) {
      tooltipX = x - width - 15;
    }
    if (tooltipY < 10) tooltipY = 10;
    if (tooltipY + height > container.clientHeight - 10) {
      tooltipY = container.clientHeight - height - 10;
    }

    // Rarity colors
    const rarityColors: Record<string, number> = {
      common: 0x9eb6ff,
      uncommon: 0x4ade80,
      rare: 0xa855f7,
      legendary: 0xfbbf24,
    };
    const borderColor = rarityColors[joker.rarity] ?? 0x9eb6ff;

    // Tooltip background
    const bg = new Graphics();
    bg.beginFill(0x0a0f1e, 0.95);
    bg.lineStyle(2, borderColor, 0.8);
    bg.drawRoundedRect(0, 0, width, height, 8);
    bg.endFill();
    bg.position.set(tooltipX, tooltipY);
    tooltipLayer.addChild(bg);

    // Joker name
    const nameText = new Text(
      joker.name,
      new TextStyle({
        fontFamily: '"Space Grotesk", system-ui',
        fontSize: 12,
        fill: 0xffffff,
        fontWeight: 'bold',
      }),
    );
    nameText.position.set(tooltipX + padding, tooltipY + padding);
    tooltipLayer.addChild(nameText);

    // Rarity badge
    const rarityText = new Text(
      joker.rarity.toUpperCase(),
      new TextStyle({
        fontFamily: 'system-ui',
        fontSize: 9,
        fill: borderColor,
      }),
    );
    rarityText.position.set(tooltipX + padding, tooltipY + padding + 16);
    tooltipLayer.addChild(rarityText);

    // Position and add effect texts with cumulative offset
    let currentY = tooltipY + 42;
    effectTexts.forEach((effectText) => {
      effectText.position.set(tooltipX + padding, currentY);
      tooltipLayer.addChild(effectText);
      currentY += effectText.height + effectSpacing;
    });

    tooltipLayer.visible = true;
  };

  // Hide joker tooltip
  const hideJokerTooltip = () => {
    const tooltipLayer = tooltipLayerRef.current;
    if (tooltipLayer) {
      tooltipLayer.visible = false;
      tooltipLayer.removeChildren();
    }
    activeJokerRef.current = null;
  };

  // Pulse animation for activated jokers
  const pulseJoker = (sprite: Container) => {
    const app = appRef.current;
    if (!app) return;

    const originalScale = sprite.scale.x;
    let elapsed = 0;

    const tick = (delta: number) => {
      elapsed += delta / 60;
      const pulse = 1 + Math.sin(elapsed * 10) * 0.12;
      sprite.scale.set(originalScale * pulse);
      sprite.alpha = 0.7 + Math.sin(elapsed * 10) * 0.3;

      if (elapsed > 0.6) {
        app.ticker.remove(tick);
        activeTickersRef.current.delete(tick);
        sprite.scale.set(originalScale);
        sprite.alpha = 1;
      }
    };
    activeTickersRef.current.add(tick);
    app.ticker.add(tick);
  };

  useEffect(() => {
    const cardsLayer = cardsLayerRef.current;
    // Wait for textures to load before rendering cards with suit textures
    if (!cardsLayer || !texturesReady) return;
    cardsLayer.removeChildren();

    const currentHandOwner = state?.currentHandOwner;
    const currentHand = state?.currentHand ?? [];
    const hasActiveHand = Boolean(currentHandOwner && currentHand.length);
    const lastTurn = state?.timeline?.[state.timeline.length - 1];
    const fallbackCards = lastTurn?.cardsPlayed ?? [];
    const visibleCards = hasActiveHand ? currentHand : fallbackCards;
    const signature = hasActiveHand
      ? `hand:${currentHand.map((card) => card.id).join(',')}`
      : `timeline:${lastTurn?.sequence ?? -1}`;
    const shouldAnimate = signature !== previousSignatureRef.current;
    previousSignatureRef.current = signature;

    // Check for new combo to celebrate
    if (lastTurn && !hasActiveHand) {
      const comboKey = `${lastTurn.sequence}:${lastTurn.breakdown.combo.name}`;
      if (
        comboKey !== previousComboRef.current &&
        lastTurn.breakdown.combo.name !== 'Exit Liquidity'
      ) {
        previousComboRef.current = comboKey;
        // Use Balatro-style announcement for better hands
        const score = lastTurn.breakdown.total;
        if (score > 50) {
          announceHandType(lastTurn.breakdown.combo.name, score);
        } else {
          showComboCelebration(lastTurn.breakdown.combo.name, score);
        }
        // Dynamic shake based on score magnitude
        dynamicScreenShake(score);
      }
    }

    visibleCards.forEach((card, index) => {
      const sprite = createCardSprite(card, index, visibleCards.length, {
        selected: hasActiveHand && selectedCardIds.includes(card.id),
        isActiveHand: hasActiveHand,
        viewportWidth,
        viewportHeight,
      });
      sprite.name = card.id;

      // Make cards clickable when interactive
      if (isInteractive && hasActiveHand && onCardClick) {
        sprite.eventMode = 'static';
        sprite.cursor = 'pointer';
        sprite.on('pointerdown', () => onCardClick(card.id));
      }

      cardsLayer.addChild(sprite);
      animateCardIn(sprite, index, shouldAnimate);
    });

    const title = titleRef.current;
    if (title) {
      const timeDisplay =
        typeof timeRemainingSeconds === 'number' && timeRemainingSeconds >= 0
          ? ` (${timeRemainingSeconds}s)`
          : '';
      if (hasActiveHand && currentHandOwner) {
        title.text = `✦ ${currentHandOwner.slice(0, 6)}…${timeDisplay}`;
      } else if (lastTurn) {
        title.text = `✦ ${lastTurn.breakdown.combo.name.toUpperCase()} ✦`;
      } else {
        title.text = '✦ SOLANTE DRAW ✦';
      }
    }

    const deckState = currentHandOwner ? state?.deckStates?.[currentHandOwner] : undefined;
    if (drawLabelRef.current) {
      if (deckState && hasActiveHand) {
        drawLabelRef.current.text = `◈ DECK ${deckState.drawPile.length}`;
        drawLabelRef.current.alpha = 1;
      } else {
        drawLabelRef.current.text = '◈ DECK —';
        drawLabelRef.current.alpha = 0.5;
      }
    }
    if (discardLabelRef.current) {
      if (deckState && hasActiveHand) {
        discardLabelRef.current.text = `◈ VOID ${deckState.discardPile.length}`;
        discardLabelRef.current.alpha = 0.85;
      } else {
        discardLabelRef.current.text = '◈ VOID —';
        discardLabelRef.current.alpha = 0.4;
      }
    }
  }, [
    state,
    selectedCardIds,
    timeRemainingSeconds,
    isInteractive,
    onCardClick,
    texturesReady,
    viewportWidth,
    viewportHeight,
  ]);

  // Render jokers (Balatro-style row below cards)
  useEffect(() => {
    const jokersLayer = jokersLayerRef.current;
    const app = appRef.current;
    if (!jokersLayer || !app) return;

    // Check if jokers actually changed (for animation decision)
    const jokersSignature = jokers.map((j) => `${j.id}:${j.evolution?.tier ?? 'base'}`).join(',');
    const jokersChanged = jokersSignature !== previousJokersRef.current;
    previousJokersRef.current = jokersSignature;

    // Always clear and re-render to ensure persistence across turn changes
    jokersLayer.removeChildren();

    // Render each joker
    jokers.forEach((joker, index) => {
      const sprite = createJokerSprite(joker, index, jokers.length, viewportWidth, viewportHeight);
      sprite.name = joker.id;

      // Make joker interactive for hover tooltip
      sprite.eventMode = 'static';
      sprite.cursor = 'pointer';
      sprite.on('pointerover', (e) => {
        showJokerTooltip(joker, e.global.x, e.global.y);
      });
      sprite.on('pointermove', (e) => {
        if (activeJokerRef.current?.id === joker.id) {
          showJokerTooltip(joker, e.global.x, e.global.y);
        }
      });
      sprite.on('pointerout', () => {
        hideJokerTooltip();
      });

      jokersLayer.addChild(sprite);

      // Only animate entry when jokers actually changed (new joker added)
      if (jokersChanged) {
        const targetY = sprite.y;
        const targetAlpha = sprite.alpha;
        sprite.y = targetY + 30;
        sprite.alpha = 0;

        let elapsed = 0;
        const delay = index * 0.1;
        const duration = 0.3;

        const tick = (delta: number) => {
          elapsed += delta / 60;
          if (elapsed < delay) return;
          const t = Math.min(1, (elapsed - delay) / duration);
          const eased = 1 - Math.pow(1 - t, 3); // ease out cubic

          sprite.alpha = eased * targetAlpha;
          sprite.y = targetY + (1 - eased) * 30;

          if (t >= 1) {
            app.ticker.remove(tick);
            activeTickersRef.current.delete(tick);
            sprite.y = targetY;
            sprite.alpha = targetAlpha;
          }
        };
        activeTickersRef.current.add(tick);
        app.ticker.add(tick);
      }
    });
  }, [jokers, state?.currentHandSequence, viewportWidth, viewportHeight]);

  // Trigger pulse animation for activated jokers
  useEffect(() => {
    const jokersLayer = jokersLayerRef.current;
    if (!jokersLayer || activatedJokerIds.length === 0) return;

    activatedJokerIds.forEach((jokerId) => {
      const sprite = jokersLayer.children.find((child) => child.name === jokerId);
      if (sprite && sprite instanceof Container) {
        pulseJoker(sprite);
      }
    });
  }, [activatedJokerIds]);

  // Responsive min-height: smaller in portrait to leave room for tabs
  const minHeightClass = isPortrait
    ? 'min-h-[180px] sm:min-h-[200px]'
    : 'min-h-[360px] md:min-h-[420px] xl:min-h-[480px]';

  return (
    <div
      ref={containerRef}
      className={`arcade-panel h-full w-full rounded-2xl border-2 border-arcade-cyan/40 shadow-arcade ${minHeightClass}`}
      style={{
        background: 'linear-gradient(135deg, rgba(5, 8, 16, 0.95) 0%, rgba(10, 15, 30, 0.9) 100%)',
      }}
    />
  );
};

// Get combo-specific color
function getComboColor(comboName: string): number {
  const colors: Record<string, number> = {
    'Twin Rugs': 0x4ef6ff,
    'Double Bottom': 0x22d3ee,
    'Triple Top': 0xa855f7,
    'Bull Run': 0xf59e0b,
    'Chain Maxi': 0xec4899,
    'VC Full Bag': 0x8b5cf6,
    'Sybil Attack': 0xfcd34d,
    'The Singularity': 0xff4f5e,
  };
  return colors[comboName] ?? 0x4ef6ff;
}
